import { Box, Card, Divider, Stack, Tooltip, Typography } from "@mui/material";
import { MetricRangeFormValue, NewSitesMetrics, SiteMetricsValuesData } from "../../types";

import Chip from "@mui/material/Chip";
import { formatMetricLabel } from "../helpers";

type MetricsSummaryProps = {
  data: NewSitesMetrics;
};

function MetricsValuesDisplay({
  metricsKey,
  metricsValuesData,
}: {
  metricsKey: string;
  metricsValuesData: MetricRangeFormValue;
}) {
  const isFlat = metricsValuesData.range_min === metricsValuesData.range_max;

  return (
    <Stack
      direction="row"
      alignItems="center"
      px={2}
      py={1.25}
      gap={2}
      sx={{
        "&:not(:last-child)": { borderBottom: "0.5px solid", borderColor: "divider" },
      }}
    >
      <Typography variant="body2" color="text.secondary" sx={{ flex: 1, textTransform: "capitalize", fontSize: 13 }}>
        {formatMetricLabel(metricsKey)}
      </Typography>

      <Stack direction="row" alignItems="center" gap={1}>
        <Typography variant="body2" fontWeight={500} sx={{ fontSize: 13, minWidth: 36, textAlign: "right" }}>
          {metricsValuesData.range_min}
        </Typography>
        <Typography variant="caption" color="text.disabled" sx={{ fontSize: 13, px: 0.5 }}>
          →
        </Typography>
        <Typography variant="body2" fontWeight={500} sx={{ fontSize: 13, minWidth: 36 }}>
          {metricsValuesData.range_max}
        </Typography>
      </Stack>

      <Chip
        label={metricsValuesData.direction}
        size="small"
        color="success"
        variant="outlined"
        sx={{ fontSize: 10, height: 20, "& .MuiChip-label": { px: 1 } }}
      />

      {isFlat ? (
        <Tooltip title="min and max are equal" placement="top">
          <Typography variant="caption" color="warning.main" sx={{ fontSize: 14, lineHeight: 1, cursor: "default" }}>
            ⚠
          </Typography>
        </Tooltip>
      ) : (
        <Box sx={{ width: 20 }} />
      )}
    </Stack>
  );
}

function SiteDisplayMetrics({ siteId, metricsValues }: { siteId: string; metricsValues: SiteMetricsValuesData }) {
  return (
    <Card variant="outlined" sx={{ mb: 2 }}>
      <Stack direction="row" alignItems="center" gap={1} px={2} py={1.5}>
        <Chip label="Site" size="small" color="primary" variant="outlined" sx={{ fontSize: 11 }} />
        <Typography variant="subtitle1" fontWeight={500}>
          {siteId}
        </Typography>
      </Stack>
      <Divider />
      {Object.entries(metricsValues).map(([key, metricsValuesData]) => (
        <MetricsValuesDisplay key={key} metricsKey={key} metricsValuesData={metricsValuesData} />
      ))}
    </Card>
  );
}

function MetricsSummary({ data }: MetricsSummaryProps) {
  return (
    <Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        You are about to create the following metrics:
      </Typography>
      {Object.entries(data).map(([siteId, metricsValues]) => (
        <SiteDisplayMetrics key={siteId} siteId={siteId} metricsValues={metricsValues} />
      ))}
    </Box>
  );
}

export default MetricsSummary;
